import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import UbiquitiSiteContext from '@totem/components/ubiquiti/sites/ubiquitiSiteContainerContext';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { UBIQUITI_SITES_ENDPOINT } from '@totem/utilities/endpoints';

import { IBDSiteHostResults } from '../types';

type Props = {
  refresh?: boolean;
  onRecordTotalChanged?: (total: number) => void;
  onDataRefreshRequested?: () => void;
  children?: ReactNode;
};

const UbiquitiSitesContainer = ({
  refresh,
  onRecordTotalChanged,
  onDataRefreshRequested,
  children,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
  } = useContext(InputContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [siteData, setSiteData] = useState<IBDSiteHostResults>(null);
  const fetchAbortController = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isNotNull(onRecordTotalChanged)) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    if (refresh && isNotNull(onDataRefreshRequested)) {
      setRefreshData(true);

      onDataRefreshRequested();
    }
  }, [refresh, onDataRefreshRequested]);

  useEffect(() => {
    setRefreshData(true);
  }, [
    userProfile.organization.id,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
  ]);

  useEffect(() => {
    if (refreshData) {
      fetchAbortController.current?.abort();

      const controller = new AbortController();

      fetchAbortController.current = controller;

      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();

      fetch(`${UBIQUITI_SITES_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((result: IBDSiteHostResults) => {
          setSiteData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsLoading(false);
        })
        .catch((err: Error) => {
          if (err.name !== 'AbortError') {
            setIsLoading(false);
          }
        });
    }
  }, [refreshData, refresh, getApiToken]);

  return (
    <UbiquitiSiteContext.Provider
      value={{
        loading: isLoading,
        siteData,
        totalRecords,
      }}
    >
      <div>{children}</div>
    </UbiquitiSiteContext.Provider>
  );
};

export default UbiquitiSitesContainer;
